<script setup lang="ts">
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import CommentView from '../CommentView.vue';
import { useData } from '../../_composables/useData';
import AddComment from './CommentModal/AddComment.vue';
import HotelPoints from '@components/util/hotel-card/HotelPoints.vue';
import ExpandableCard from '@components/util/ExpandableCard.vue';

const { hotelPackage } = useData();
const { login } = useAuth();
const { open } = useModal({ component: AddComment });
async function addComment() {
    const loggedIn = await login({
        message: 'برای افزودن نظر جدید، وارد شوید.',
    });
    if (loggedIn) {
        open({
            hotelName: hotelPackage.value.title,
            hotelId: hotelPackage.value.id ?? 0,
        });
    }
}
const { isMobile } = useSize();
const comments = computed(() => {
    if (hotelPackage.value.hotelName === 'jamali-mashhad') {
        return hotelPackage.value.comments.filter(
            c => c.rating && c.rating > 4
        );
    }
    return hotelPackage.value.comments;
});
const sortItems: SelectItemType<string>[] = [
    { label: 'جدیدترین', id: 'new' },
    { label: 'مثبت‌ترین', id: 'positive' },
];
const sort = ref<'new' | 'positive'>('new');
const sortedComments = computed(() =>
    comments.value.slice().sort((a, b) => {
        switch (sort.value) {
            case 'new':
                return (
                    new Date(b.postedOn ?? '').getTime() -
                    new Date(a.postedOn ?? '').getTime()
                );
            case 'positive':
                return (b.rating ?? 0) - (a.rating ?? 0);
            default:
                return 0;
        }
    })
);
</script>
<template>
    <div class="section-container">
        <div class="title">
            <div class="points">
                <mr-typography
                    size="x-large"
                    weight="700"
                    type="h2"
                    white-space="nowrap"
                >
                    نظرات کاربران
                </mr-typography>
                <HotelPoints
                    v-if="sortedComments.length"
                    :value="hotelPackage.points10 || 0"
                    size="large"
                />
            </div>
            <div class="sorting">
                <mr-typography size="large">مرتب‌سازی:</mr-typography>
                <mr-select
                    v-model="sort"
                    size="free"
                    :options="sortItems"
                    hide-description
                    hide-label
                />
            </div>
        </div>
        <ExpandableCard
            :default-height="isMobile ? 336 : 470"
            more-title="نظرات بیشتر"
            :data-value="comments.length"
            :disabled="!comments.length"
            fogies
        >
            <template #disabled>
                <mr-typography
                    v-if="comments.length === 0"
                    :size="{ desktop: 'x-large', mobile: 'medium' }"
                    align="center"
                >
                    برای این هتل هنوز هیچ نظری ثبت نشده است.
                </mr-typography>
            </template>
            <div class="comments-content">
                <CommentView
                    v-for="(comment, key) in sortedComments"
                    :key
                    :item="comment"
                    class="comment-item"
                />
            </div>
            <template #extra-button>
                <div class="add-comment">
                    <mr-button
                        :left-icon="faPlus"
                        size="medium"
                        no-wrap
                        @click="addComment"
                    >
                        افزودن نظر
                    </mr-button>
                </div>
            </template>
        </ExpandableCard>
    </div>
</template>
<style scoped lang="scss">
.title {
    margin-bottom: 8px;
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 20px;
    justify-content: space-between;
    @include mobile-only {
        margin-bottom: 0;
        flex-wrap: wrap;
    }
    .points {
        display: flex;
        align-items: center;
        @include mobile-only {
            justify-content: space-between;
            width: 100%;
        }
    }
    .sorting {
        display: flex;
        @include mobile-only {
            justify-content: space-between;
            width: 100%;
        }
    }
}
.comments-content {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    padding-bottom: 30px;
    @include mobile-only {
        gap: 8px;
    }
    .comment-item {
        flex-basis: calc(50% - 10px);
        max-width: calc(50% - 10px);
        @include mobile-only {
            flex-basis: 100%;
            max-width: 100%;
        }
    }
}
.add-comment {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
}
</style>
